import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEdit, 
  faTrash, 
  faTags,
  faPercentage,
  faDollarSign,
  faCalendarAlt,
  faCheck,
  faTimes,
  faGift
} from '@fortawesome/free-solid-svg-icons';
import { db } from '../../firebase';
import { 
  collection, 
  addDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  doc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';

const CouponsSection = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    type: 'percentage', // 'percentage', 'fixed', 'free'
    value: 0,
    minOrderAmount: 0,
    maxUses: '',
    usedCount: 0,
    expiryDate: '',
    isActive: true,
    isTesting: false
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const couponsQuery = query(
        collection(db, 'coupons'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(couponsQuery);
      const couponsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCoupons(couponsData);
    } catch (error) {
      console.error('Error fetching coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.code.trim()) {
      newErrors.code = 'Coupon code is required';
    } else if (formData.code.length < 3) {
      newErrors.code = 'Coupon code must be at least 3 characters';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (formData.type !== 'free' && (!formData.value || formData.value <= 0)) {
      newErrors.value = 'Value must be greater than 0';
    }
    
    if (formData.type === 'percentage' && formData.value > 100) {
      newErrors.value = 'Percentage cannot exceed 100%';
    }
    
    if (formData.minOrderAmount < 0) {
      newErrors.minOrderAmount = 'Minimum order amount cannot be negative';
    }
    
    if (formData.maxUses && formData.maxUses <= 0) {
      newErrors.maxUses = 'Max uses must be greater than 0 if specified';
    }
    
    if (!formData.expiryDate) {
      newErrors.expiryDate = 'Expiry date is required';
    } else if (new Date(formData.expiryDate) <= new Date()) {
      newErrors.expiryDate = 'Expiry date must be in the future';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const couponData = {
        ...formData,
        code: formData.code.toUpperCase(),
        value: formData.type === 'free' ? 100 : Number(formData.value),
        minOrderAmount: Number(formData.minOrderAmount),
        maxUses: formData.maxUses ? Number(formData.maxUses) : null,
        updatedAt: new Date().toISOString() // Use regular timestamp instead of serverTimestamp for now
      };

      if (editingCoupon) {
        await updateDoc(doc(db, 'coupons', editingCoupon.id), couponData);
      } else {
        await addDoc(collection(db, 'coupons'), {
          ...couponData,
          createdAt: new Date().toISOString() // Use regular timestamp instead of serverTimestamp for now
        });
      }

      resetForm();
      fetchCoupons();
    } catch (error) {
      console.error('Error saving coupon:', error);

      let errorMessage = 'Failed to save coupon. Please try again.';
      
      // Provide more specific error messages
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please ensure you are logged in as an admin.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Firebase service unavailable. Please check your internet connection.';
      } else if (error.code === 'unauthenticated') {
        errorMessage = 'Authentication required. Please log in again.';
      }
      
      setErrors({ submit: errorMessage });
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      description: coupon.description,
      type: coupon.type,
      value: coupon.value,
      minOrderAmount: coupon.minOrderAmount || 0,
      maxUses: coupon.maxUses || '',
      usedCount: coupon.usedCount || 0,
      expiryDate: coupon.expiryDate,
      isActive: coupon.isActive,
      isTesting: coupon.isTesting || false
    });
    setShowForm(true);
  };

  const handleDelete = async (couponId) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      try {
        await deleteDoc(doc(db, 'coupons', couponId));
        fetchCoupons();
      } catch (error) {
        console.error('Error deleting coupon:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      type: 'percentage',
      value: 0,
      minOrderAmount: 0,
      maxUses: '',
      usedCount: 0,
      expiryDate: '',
      isActive: true,
      isTesting: false
    });
    setEditingCoupon(null);
    setShowForm(false);
    setErrors({});
  };

  const formatValue = (coupon) => {
    if (coupon.type === 'free') {
      return 'FREE';
    } else if (coupon.type === 'percentage') {
      return `${coupon.value}%`;
    } else {
      return `$${coupon.value}`;
    }
  };

  const getStatusBadge = (coupon) => {
    const now = new Date();
    const expiry = new Date(coupon.expiryDate);
    
    if (!coupon.isActive) {
      return <span className="status-badge inactive">Inactive</span>;
    } else if (expiry <= now) {
      return <span className="status-badge expired">Expired</span>;
    } else if (coupon.maxUses && coupon.usedCount >= coupon.maxUses) {
      return <span className="status-badge limit-reached">Limit Reached</span>;
    } else {
      return <span className="status-badge active">Active</span>;
    }
  };

  return (
    <div className="coupons-section">
      <div className="section-header">
        <h2><FontAwesomeIcon icon={faTags} /> Coupons Management</h2>
        <div className="header-actions">
          <button
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Coupon
          </button>
        </div>
      </div>

      {showForm && (
        <div className="form-modal">
          <div className="form-modal-content">
            <div className="form-header">
              <h3>{editingCoupon ? 'Edit Coupon' : 'Add New Coupon'}</h3>
              <button className="close-btn" onClick={resetForm}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="coupon-form">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="code">Coupon Code *</label>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    placeholder="e.g., SAVE20, FREE100"
                    className={errors.code ? 'error' : ''}
                  />
                  {errors.code && <span className="error-message">{errors.code}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="type">Discount Type *</label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                  >
                    <option value="percentage">Percentage Off</option>
                    <option value="fixed">Fixed Amount Off</option>
                    <option value="free">Free (100% Off)</option>
                  </select>
                </div>

                {formData.type !== 'free' && (
                  <div className="form-group">
                    <label htmlFor="value">
                      {formData.type === 'percentage' ? 'Percentage' : 'Amount'} *
                    </label>
                    <div className="input-with-icon">
                      <FontAwesomeIcon 
                        icon={formData.type === 'percentage' ? faPercentage : faDollarSign} 
                      />
                      <input
                        type="number"
                        id="value"
                        name="value"
                        value={formData.value}
                        onChange={handleInputChange}
                        min="0"
                        max={formData.type === 'percentage' ? 100 : undefined}
                        step={formData.type === 'percentage' ? 1 : 0.01}
                        className={errors.value ? 'error' : ''}
                      />
                    </div>
                    {errors.value && <span className="error-message">{errors.value}</span>}
                  </div>
                )}

                <div className="form-group full-width">
                  <label htmlFor="description">Description *</label>
                  <input
                    type="text"
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief description of the coupon"
                    className={errors.description ? 'error' : ''}
                  />
                  {errors.description && <span className="error-message">{errors.description}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="minOrderAmount">Minimum Order Amount</label>
                  <div className="input-with-icon">
                    <FontAwesomeIcon icon={faDollarSign} />
                    <input
                      type="number"
                      id="minOrderAmount"
                      name="minOrderAmount"
                      value={formData.minOrderAmount}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className={errors.minOrderAmount ? 'error' : ''}
                    />
                  </div>
                  {errors.minOrderAmount && <span className="error-message">{errors.minOrderAmount}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="maxUses">Maximum Uses (optional)</label>
                  <input
                    type="number"
                    id="maxUses"
                    name="maxUses"
                    value={formData.maxUses}
                    onChange={handleInputChange}
                    min="1"
                    placeholder="Leave empty for unlimited"
                    className={errors.maxUses ? 'error' : ''}
                  />
                  {errors.maxUses && <span className="error-message">{errors.maxUses}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="expiryDate">Expiry Date *</label>
                  <div className="input-with-icon">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    <input
                      type="date"
                      id="expiryDate"
                      name="expiryDate"
                      value={formData.expiryDate}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      className={errors.expiryDate ? 'error' : ''}
                    />
                  </div>
                  {errors.expiryDate && <span className="error-message">{errors.expiryDate}</span>}
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                    />
                    <span>Active</span>
                  </label>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isTesting"
                      checked={formData.isTesting}
                      onChange={handleInputChange}
                    />
                    <span>Testing Coupon</span>
                  </label>
                </div>
              </div>

              {errors.submit && (
                <div className="error-message">{errors.submit}</div>
              )}

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <FontAwesomeIcon icon={faCheck} /> 
                  {editingCoupon ? 'Update' : 'Create'} Coupon
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="coupons-list">
        {loading ? (
          <div className="loading">Loading coupons...</div>
        ) : coupons.length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon={faTags} />
            <h3>No Coupons Found</h3>
            <p>Create your first coupon to get started</p>
          </div>
        ) : (
          <div className="coupons-grid">
            {coupons.map(coupon => (
              <div key={coupon.id} className="coupon-card">
                <div className="coupon-header">
                  <div className="coupon-code">
                    <FontAwesomeIcon icon={faTags} />
                    {coupon.code}
                  </div>
                  <div className="coupon-actions">
                    <button 
                      className="btn-icon" 
                      onClick={() => handleEdit(coupon)}
                      title="Edit coupon"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button 
                      className="btn-icon delete" 
                      onClick={() => handleDelete(coupon.id)}
                      title="Delete coupon"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
                
                <div className="coupon-body">
                  <h4>{coupon.description}</h4>
                  <div className="coupon-value">{formatValue(coupon)}</div>
                  
                  <div className="coupon-details">
                    {coupon.minOrderAmount > 0 && (
                      <div className="detail">
                        Min Order: ${coupon.minOrderAmount}
                      </div>
                    )}
                    
                    {coupon.maxUses && (
                      <div className="detail">
                        Uses: {coupon.usedCount || 0}/{coupon.maxUses}
                      </div>
                    )}
                    
                    <div className="detail">
                      Expires: {new Date(coupon.expiryDate).toLocaleDateString()}
                    </div>
                    
                    {coupon.isTesting && (
                      <div className="testing-badge">
                        <FontAwesomeIcon icon={faGift} /> Testing
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="coupon-footer">
                  {getStatusBadge(coupon)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CouponsSection; 